import { useEffect, useState, type ReactElement } from 'react';
import { Badge, Group, Loader, Stack, Text } from '@mantine/core';
import { useDIContainer } from '@/container/RouteContainerProvider';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { RecordDrawerSectionProps } from '@/shared/surfaces/RecordDrawerExtensions';
import { ClassificationService, type Classification } from '../classification/ClassificationService';
import { listedGovernancePolicies } from './PolicyDerivation';
import { policyFieldName } from './GovernancePolicyData';
import { formatVerdict, isConcerningValue } from './PolicyVerdict';

let policiesPromise: Promise<Classification[]> | null = null;

/** The live governance policies, fetched once per session and shared by every drawer instance. */
function loadPolicies(service: ClassificationService): Promise<Classification[]> {
    policiesPromise ??= service
        .list()
        .then(listedGovernancePolicies)
        .catch(() => {
            policiesPromise = null;
            return [];
        });
    return policiesPromise;
}

interface VerdictRow {
    id: string;
    name: string;
    verdict: string;
    concern: boolean;
}

/**
 * The record drawer's Governance tab: every live policy that classified this
 * turn, with its verdict and whether it is concerning. Policy verdict
 * dimensions aren't part of the default record projection, so this re-fetches
 * the single turn with the policy field keys hydrated. Contributed to the
 * shared record drawer via the composition root's extension registry.
 */
export default function RecordPolicyVerdicts({ record }: RecordDrawerSectionProps): ReactElement {
    const container = useDIContainer();
    const [rows, setRows] = useState<VerdictRow[] | null>(null);

    useEffect((): (() => void) => {
        let cancelled = false;
        setRows(null);
        const load = async (): Promise<void> => {
            const policies = await loadPolicies(container.resolve(ClassificationService));
            const withField = policies.filter((policy) => policyFieldName(policy) !== null);
            if (withField.length === 0) {
                if (!cancelled) setRows([]);
                return;
            }
            const gateway = container.resolve<IAnalyticsGateway>(IAnalyticsGatewayToken);
            const result = await gateway.records({
                source: 'interactions',
                filters: [{ field: 'id', values: [record.id] }],
                dimensionKeys: withField.map((policy) => policyFieldName(policy)).filter((name): name is string => name !== null),
                limit: 1,
                ignoreGlobalFilters: true,
            });
            const dimensions = result.records[0]?.dimensions ?? {};
            const next = withField.flatMap((policy) => {
                const fieldName = policyFieldName(policy);
                const dim = fieldName === null ? undefined : dimensions[fieldName];
                if (dim === undefined) return [];
                return [
                    {
                        id: policy.id,
                        name: policy.name,
                        verdict: formatVerdict(policy.outputType, dim.value, dim.valueNum, policy.outputConfig),
                        concern: isConcerningValue(policy.outputType, dim.value, policy.outputConfig),
                    },
                ];
            });
            if (!cancelled) setRows(next);
        };
        void load().catch(() => {
            if (!cancelled) setRows([]);
        });
        return (): void => {
            cancelled = true;
        };
    }, [container, record.id]);

    if (rows === null) {
        return (
            <Group gap="xs" py="sm" justify="center">
                <Loader size="xs" />
                <Text size="xs" c="dimmed">
                    Checking policies…
                </Text>
            </Group>
        );
    }
    if (rows.length === 0) {
        return (
            <Text size="xs" c="dimmed" py="sm">
                No governance policies evaluated this turn.
            </Text>
        );
    }

    return (
        <Stack gap={8}>
            {rows.map((row) => (
                <Group key={row.id} justify="space-between" wrap="nowrap" gap="sm">
                    <Text size="xs" fw={600} truncate>
                        {row.name}
                    </Text>
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Text size="xs" c="dimmed" ff="monospace">
                            {row.verdict}
                        </Text>
                        <Badge size="xs" radius="sm" variant="light" color={row.concern ? 'red' : 'teal'}>
                            {row.concern ? 'Fail' : 'Pass'}
                        </Badge>
                    </Group>
                </Group>
            ))}
        </Stack>
    );
}
